import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Live transcription through the Web Speech API.
 *
 * Independent of the recorder on purpose. Recognition is missing in Firefox and
 * flaky everywhere, so it is treated as an enhancement: when it works the parent
 * watches their words appear, and when it does not they still have a recording
 * and a text field. Capture never becomes impossible.
 *
 * The committed text lives in a ref as well as in state. `stop()` is called from
 * a pointer handler that may hold a render-old closure, and returning a stale
 * transcript would silently drop the last thing the parent said.
 */

export interface SpeechRecognitionState {
  supported: boolean
  listening: boolean
  /** Text the engine has committed. */
  transcript: string
  /** The phrase still in flight. Shown differently, never lost. */
  interim: string
  /** Set only for failures worth telling the parent about. */
  error: string | null
  start: () => void
  /** Resolves with everything heard, including the unflushed phrase. */
  stop: () => Promise<string>
  reset: () => void
}

interface RecognitionLike extends EventTarget {
  continuous: boolean
  interimResults: boolean
  maxAlternatives: number
  lang: string
  start(): void
  stop(): void
  abort(): void
  onresult: ((e: any) => void) | null
  onerror: ((e: any) => void) | null
  onend: (() => void) | null
}

type RecognitionCtor = new () => RecognitionLike

function getCtor(): RecognitionCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as any
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

export function isSpeechRecognitionSupported(): boolean {
  return !!getCtor()
}

/** Chrome ends a session on every pause; a few restarts are normal, a storm is not. */
const MAX_RESTARTS = 40
/** Long enough for a final result to land, short enough to feel immediate. */
const FLUSH_MS = 160

export function useSpeechRecognition(): SpeechRecognitionState {
  const [supported] = useState(() => isSpeechRecognitionSupported())
  const [listening, setListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [interim, setInterim] = useState('')
  const [error, setError] = useState<string | null>(null)

  const recRef = useRef<RecognitionLike | null>(null)
  const finalRef = useRef('')
  const interimRef = useRef('')
  const wantedRef = useRef(false)
  const restartsRef = useRef(0)

  const detach = useCallback(() => {
    const rec = recRef.current
    if (!rec) return
    rec.onresult = null
    rec.onerror = null
    rec.onend = null
    try { rec.abort() } catch { /* already finished */ }
    recRef.current = null
  }, [])

  useEffect(
    () => () => {
      wantedRef.current = false
      detach()
    },
    [detach],
  )

  const start = useCallback(() => {
    const Ctor = getCtor()
    finalRef.current = ''
    interimRef.current = ''
    setTranscript('')
    setInterim('')
    setError(null)
    restartsRef.current = 0

    if (!Ctor) {
      wantedRef.current = false
      return
    }

    detach()
    wantedRef.current = true

    const rec = new Ctor()
    rec.continuous = true
    rec.interimResults = true
    rec.maxAlternatives = 1
    rec.lang = navigator.language || 'en-US'

    rec.onresult = (e: any) => {
      let committed = ''
      let pending = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const result = e.results[i]
        const text = result?.[0]?.transcript ?? ''
        if (result?.isFinal) committed += text
        else pending += text
      }
      if (committed) {
        finalRef.current = `${finalRef.current} ${committed}`.replace(/\s+/g, ' ').trim()
        setTranscript(finalRef.current)
      }
      interimRef.current = pending
      setInterim(pending)
    }

    rec.onerror = (e: any) => {
      const kind = e?.error
      // 'no-speech' and 'aborted' are ordinary parts of a pause or a cancel.
      if (kind === 'not-allowed' || kind === 'service-not-allowed') {
        wantedRef.current = false
        setError('Live transcription is off. Your recording is still being saved.')
      } else if (kind === 'network') {
        setError('Transcription needs a connection. Your recording is still being saved.')
      }
    }

    rec.onend = () => {
      if (!wantedRef.current || recRef.current !== rec) return
      if (restartsRef.current >= MAX_RESTARTS) return
      restartsRef.current += 1
      try { rec.start() } catch { /* racing a deliberate stop */ }
    }

    recRef.current = rec
    try {
      rec.start()
      setListening(true)
    } catch {
      // Already running from a previous session that has not finished ending.
      setListening(true)
    }
  }, [detach])

  const stop = useCallback(async (): Promise<string> => {
    wantedRef.current = false
    const rec = recRef.current

    if (rec) {
      rec.onend = null
      try { rec.stop() } catch { /* already stopped */ }
      // One beat for the engine to commit the phrase it is holding.
      await new Promise((r) => setTimeout(r, FLUSH_MS))
    }

    const text = `${finalRef.current} ${interimRef.current}`.replace(/\s+/g, ' ').trim()
    detach()
    setListening(false)
    interimRef.current = ''
    setInterim('')
    if (text) setTranscript(text)
    return text
  }, [detach])

  const reset = useCallback(() => {
    wantedRef.current = false
    detach()
    finalRef.current = ''
    interimRef.current = ''
    setTranscript('')
    setInterim('')
    setError(null)
    setListening(false)
  }, [detach])

  return { supported, listening, transcript, interim, error, start, stop, reset }
}
